import React, { useState, useEffect } from 'react';
import { companyAPI } from '../services/api';
import Icon from './Icon';

const SuperAdminTokens = ({ darkMode = true }) => {
    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [note, setNote] = useState('');
    const [generating, setGenerating] = useState(false);
    const [success, setSuccess] = useState('');

    useEffect(() => {
        fetchTokens();
    }, []);

    const fetchTokens = async () => {
        try {
            setLoading(true);
            const response = await companyAPI.getRegistrationTokens();
            setTokens(response.data.tokens || []);
        } catch (err) {
            console.error('Error fetching tokens:', err);
            setError('Failed to load registration tokens');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateToken = async (e) => {
        e.preventDefault();
        setGenerating(true);
        setError('');

        try {
            await companyAPI.generateRegistrationToken(note);
            setSuccess('Token generated successfully!');
            setShowGenerateModal(false);
            setNote('');
            fetchTokens();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to generate token');
        } finally {
            setGenerating(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setSuccess('Copied to clipboard!');
        setTimeout(() => setSuccess(''), 2000);
    };

    return (
        <div style={{
            background: 'var(--bg-overlay-light)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '16px',
            padding: '24px'
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
                flexWrap: 'wrap',
                gap: '16px'
            }}>
                <div>
                    <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', color: '#ffffff' }}>Registration Invitations</h2>
                    <p style={{ margin: 0, color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
                        Manage invitation codes for new user registration
                    </p>
                </div>

                <button
                    onClick={() => setShowGenerateModal(true)}
                    className="btn btn-primary"
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                    <Icon name="add" color="white" darkMode={darkMode} size={18} />
                    Generate New Token
                </button>
            </div>

            {error && (
                <div className="error" style={{ marginBottom: '20px' }}>
                    {error}
                </div>
            )}

            {success && (
                <div className="success" style={{ marginBottom: '20px' }}>
                    {success}
                </div>
            )}

            {loading ? (
                <div className="loading">Loading tokens...</div>
            ) : tokens.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: 'rgba(255,255,255,0.5)'
                }}>
                    <p>No invitation tokens found.</p>
                    <button onClick={() => setShowGenerateModal(true)} className="btn btn-secondary" style={{ marginTop: '12px' }}>
                        Generate First Token
                    </button>
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '14px'
                    }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                                {['Token', 'Status', 'Note', 'Created By', 'Used By', 'Actions'].map(h => (
                                    <th key={h} style={{
                                        padding: '12px 8px',
                                        textAlign: 'left',
                                        color: 'rgba(255,255,255,0.5)',
                                        fontWeight: '500',
                                        fontSize: '12px',
                                        textTransform: 'uppercase'
                                    }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {tokens.map(token => (
                                <tr key={token.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                    <td style={{ padding: '12px 8px' }}>
                                        <code style={{
                                            background: 'rgba(0,0,0,0.2)',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            fontFamily: 'monospace',
                                            fontSize: '14px',
                                            color: token.is_used ? 'rgba(255,255,255,0.5)' : '#4A8C18',
                                            textDecoration: token.is_used ? 'line-through' : 'none'
                                        }}>
                                            {token.token}
                                        </code>
                                    </td>
                                    <td style={{ padding: '12px 8px' }}>
                                        <span style={{
                                            padding: '4px 10px',
                                            borderRadius: '12px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            background: token.is_used ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.15)',
                                            color: token.is_used ? '#f87171' : '#4A8C18'
                                        }}>
                                            {token.is_used ? 'Used' : 'Active'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px 8px', color: 'rgba(255,255,255,0.8)' }}>
                                        {token.note || '-'}
                                    </td>
                                    <td style={{ padding: '12px 8px', color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>
                                        {token.created_by_name || 'System'}
                                        <div style={{ fontSize: '11px', opacity: 0.7 }}>
                                            {new Date(token.created_at).toLocaleDateString()}
                                        </div>
                                    </td>
                                    <td style={{ padding: '12px 8px', color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>
                                        {token.used_by_username ? (
                                            <>
                                                {token.used_by_username}
                                                <div style={{ fontSize: '11px', opacity: 0.7 }}>
                                                    {new Date(token.used_at).toLocaleDateString()}
                                                </div>
                                            </>
                                        ) : '-'}
                                    </td>
                                    <td style={{ padding: '12px 8px' }}>
                                        {!token.is_used && (
                                            <button
                                                onClick={() => copyToClipboard(token.token)}
                                                style={{
                                                    background: 'rgba(255,255,255,0.1)',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    padding: '6px 12px',
                                                    color: '#ffffff',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                <Icon name="copy" color="white" darkMode={darkMode} size={14} />
                                                Copy
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Generate Token Modal */}
            {showGenerateModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.8)',
                        backdropFilter: 'blur(8px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1100,
                        padding: '24px'
                    }}
                    onClick={() => setShowGenerateModal(false)}
                >
                    <div
                        style={{
                            background: '#1a1a3e',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '20px',
                            padding: '28px',
                            maxWidth: '500px',
                            width: '100%'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 style={{ margin: '0 0 16px 0', color: '#ffffff' }}>Generate New Token</h3>

                        <form onSubmit={handleGenerateToken}>
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
                                    Note (optional)
                                </label>
                                <input
                                    type="text"
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder="E.g. For Comfort Dental"
                                    className="form-input"
                                    autoFocus
                                />
                                <p style={{ marginTop: '8px', fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
                                    This helps you track who the token was given to.
                                </p>
                            </div>

                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowGenerateModal(false)}
                                    className="btn btn-secondary"
                                    disabled={generating}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={generating}
                                >
                                    {generating ? 'Generating...' : 'Generate Token'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SuperAdminTokens;
